import React, { useState, useMemo }  from 'react';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { NavLink } from 'react-router-dom';
import classNames from "classnames";

import Drawer from '@material-ui/core/Drawer';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import Collapse from '@material-ui/core/Collapse';

import * as Icons from '@material-ui/icons'

import ClubSelect from 'app/components/club/ClubSelect';
import Link from 'app/components/Link';
import useApp from 'app/context/useApp';

const year = new Date().getFullYear();

const useStyles = makeStyles(theme => ({
    drawer: {
        width: theme.width.navigation,
        flexShrink: 0,
    },
    drawerMobile: {
        zIndex: theme.zIndex.drawer + "!important"
    },
    drawerPaper: {
        width: theme.width.navigation,
        boxShadow: theme.shadows[3]
    },
    drawerContainer: {
        overflow: 'auto',
    },
    menuList: {
        maxWidth: '100%'
    },
    menuItem: {
        outline: 'none',

        '& span': {
            transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            color: 'rgba(0, 0, 0, 0.44)',
        },

        '&:hover': {
            color: 'rgba(0, 0, 0, 0.74)',
            backgroundColor: '#fff'
        },
        '&:hover span': {
            color: 'rgba(0, 0, 0, 0.74)',
        },
        '& svg': {
            fill: 'rgba(0, 0, 0, 0.44)',
        },
        '&:hover svg': {
            fill: theme.palette.secondary.main,
        },
        '&:focus': {
            outline: 'none'
        },
        '&.active svg': {
            fill: theme.palette.secondary.main,
        },
        '&.active span': {
            color: theme.palette.secondary.main,
        }
    },
    menuItemNested: {
        paddingLeft: theme.spacing(6),
    },
    menuItemBottomMargin: {
        marginBottom: theme.spacing(2)
    },
    bgWhite: {
        backgroundColor: '#ffffff',
    },
    drawerFooter: {
        padding: theme.spacing(3),
        flexGrow: 1,
    },
    drawerFooterFlex: {
        display: 'flex',
        height: '100%',
        alignItems: 'flex-end'
    },
    drawerFooterItem: {
        flexGrow: 1
    },
    footerLink: {
        ...theme.typography.caption
    },
    listItemText: {
        marginTop: 3,
        marginBottom: 3
    },
    listItemTextPrimary: {
        lineHeight: 1.4,
        whiteSpace: `nowrap`,
        overflow: `hidden`,
        textOverflow: `ellipsis`,
        fontWeight: 500
    },
    listItemTextSecondary: {
        lineHeight: 1,
        fontSize: '0.725rem'
    },
    userAvatar: {
        width: 27,
        height: 27
    }
}));

function AdvancedMenuItem(props) {
    const { item, nextItem, nested } = props;

    const classes = useStyles();

    const Icon = item.icon ? Icons[item.icon] : null;

    const addMargin = (nextItem && ("nav_order" in nextItem) && ("nav_order" in item) && ((item.nav_order + 1) != nextItem.nav_order)) ? true : false;

    const itemClasses = classNames({
        [classes.menuItem]: true,
        [classes.menuItemNested]: nested,
        [classes.menuItemBottomMargin]: addMargin
    });

    return (
        <ListItem
            to={`/${item.path}`}
            component={NavLink}
            className={itemClasses}
            button={false}
            dense
            >
            {Icon &&
                <ListItemIcon className={classes.menuItemIcon}>
                    <Icon />
                </ListItemIcon>
            }
            <ListItemText primary={item.nav_label} />
        </ListItem>
    )

}

function AdvancedMenuItemWithChildren(props) {
    const { item } = props;

    const [ open, setOpen ] = useState(false);

    const classes = useStyles();

    const toggleOpen = () => {
        setOpen(!open)
    }

    const Icon = Icons[item.icon];

    const ExpandMore = Icons.ExpandMore;
    const ExpandLess = Icons.ExpandLess;

    return (
        <>
            <ListItem
                dense
                onClick={toggleOpen}
                className={classes.menuItem}
                >
                <ListItemIcon>
                    <Icon />
                </ListItemIcon>
                <ListItemText primary={item.nav_label} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <MenuList>
                    {item.children.map((child, i) => <AdvancedMenuItem key={i} item={child} nested/>)}
                </MenuList>
            </Collapse>
        </>
    )
}

export default function AppDrawer(props) {

    const { open, handleClose } = props;

    const { app } = useApp();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ clubSelectOpen, setClubSelectOpen ] = useState(false);

    const handleClubSelectOpen = () => {
        if (process.env.MIX_CLUB_SWITCH) {
            setClubSelectOpen(true);
        } else {
            history.push('/profile/clubs');
        }
    };

    const handleClubSelectClose = () => {
        setClubSelectOpen(false);
    };

    const StyledAvatar = withStyles({
        root: {
            width: 27,
            height: 27,
            fontSize: 14
        },
        img: {
            objectFit: 'contain'
        },
        colorDefault: {
            backgroundColor: app.club.branding.primary_color,
            color: theme.palette.getContrastText(app.club.branding.primary_color),
        }
    })(Avatar);

    const pages = useMemo(() => app.club.pages.map((page, i) => {
        if (page.children) return <AdvancedMenuItemWithChildren key={i} item={page} />
        return <AdvancedMenuItem key={i} item={page} nextItem={app.club.pages[i+1]} />
    }), [app.club.pages]);

    const adminPages = (app.club.admin_pages && app.club.admin_pages.length) ? useMemo(() => app.club.admin_pages.map((page, i) => {
        if (page.children) return <AdvancedMenuItemWithChildren key={i} item={page}/>
        return <AdvancedMenuItem key={i} item={page} nextItem={app.club.pages[i+1]}/>
    }), [app.club.admin_pages]) : null;

    const AccountIcon = Icons.AccountCircle;
    const HelpIcon = Icons.HelpOutline;

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            className={classes.drawer}
            classes={{
                paper: classes.drawerPaper,
                modal: classes.drawerMobile
            }}
            open={open}
            onClose={handleClose}
            >
            <MenuList className={classes.menuList} disablePadding>
                <MenuItem
                    onClick={handleClubSelectOpen}
                    selected={clubSelectOpen}
                    >
                    <ListItemAvatar>
                        <StyledAvatar
                            src={app.club.branding.icon ? app.club.branding.icon.full_url : null}
                            alt={app.club.name}
                            variant="rounded"
                            >
                            {app.club.branding.acronym_short}
                        </StyledAvatar>
                    </ListItemAvatar>
                    <ListItemText
                        className={classes.listItemText}
                        disableTypography
                        primary={
                            <Typography variant="subtitle1" color="secondary" className={classes.listItemTextPrimary}>
                                {app.club.name}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="subtitle2" color="textSecondary" className={classes.listItemTextSecondary}>
                                {process.env.MIX_CLUB_SWITCH ? 'Change Active Club' : 'View your Clubs'}
                            </Typography>
                        }
                        />
                </MenuItem>
            </MenuList>
            <Divider />
            <MenuList disablePadding>
                {pages}
            </MenuList>
            {(app.club.admin_pages && app.club.admin_pages.length > 0) &&
                <MenuList
                    disablePadding
                    subheader={<ListSubheader disableSticky>Admin</ListSubheader>}
                    >
                    {adminPages}
                </MenuList>
            }
            <MenuList
                className={classes.menuList}
                disablePadding
                subheader={<ListSubheader disableSticky>Other</ListSubheader>}
                >
                <ListItem
                    className={classes.menuItem}
                    to="/profile"
                    component={NavLink}
                    >
                    <ListItemAvatar>
                        { (app.user.avatar) ? (
                            <Avatar
                                alt={app.user.first + ' ' + app.user.last}
                                src={app.user.avatar.full_url}
                                className={classes.userAvatar}
                            />
                        ) : (
                            <AccountIcon />
                        )}
                    </ListItemAvatar>
                    <ListItemText
                        className={classes.listItemText}
                        disableTypography
                        primary={
                            <Typography variant="subtitle1" color="textSecondary" className={classes.listItemTextPrimary}>
                                {app.user.full_name}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="subtitle2" color="textSecondary" className={classes.listItemTextSecondary}>
                                View your Profile
                            </Typography>
                        }
                        />
                </ListItem>
                <ListItem
                    to="/support"
                    component={NavLink}
                    dense
                    className={classes.menuItem}
                    button={false}
                    >
                    <ListItemIcon>
                        <HelpIcon />
                    </ListItemIcon>
                    <ListItemText primary="Support" />
                </ListItem>
            </MenuList>
            <footer className={classes.drawerFooter}>
                <div className={classes.drawerFooterFlex}>
                    <div className={classes.drawerFooterItem}>
                        <Typography variant="caption" component="div">
                            &copy; {year} Club Apps, LLC
                        </Typography>
                        <Link color="secondary" className={classes.footerLink} target="_blank" to={process.env.MIX_LEGAL_BASEURL}>Terms & Privacy</Link>
                    </div>
                </div>
            </footer>
            <ClubSelect
                open={clubSelectOpen}
                clubs={app.user.clubs}
                currentClubId={app.club.id}
                onClose={handleClubSelectClose}
            />
        </Drawer>
    );
}
